import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import { Newspaper, ArrowRight } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : "");

export default function News() {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("");

  useEffect(() => {
    api.get("/articles", { params: cat ? { category: cat } : {} }).then(({ data }) => setData(data)).catch(() => setData({ items: [], categories: {} }));
  }, [cat]);

  const cats = data?.categories || {};
  const items = data?.items || [];
  const [lead, ...rest] = items;

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14">
          <div className="flex items-center gap-2 label-overline text-[#E63946]"><Newspaper className="w-4 h-4" /> The Wire</div>
          <h1 data-testid="news-title" className="font-display text-4xl sm:text-6xl uppercase tracking-tighter mt-2">CBR News</h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">Camp recaps, commitments, rankings and player spotlights from across Canadian amateur baseball.</p>
        </div>
      </section>

      <section className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto">
          <button data-testid="news-cat-all" onClick={() => setCat("")} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm whitespace-nowrap ${cat === "" ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-500"}`}>All</button>
          {Object.entries(cats).map(([v, l]) => (
            <button key={v} data-testid={`news-cat-${v}`} onClick={() => setCat(v)} className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-sm whitespace-nowrap ${cat === v ? "bg-[#0A192F] text-white" : "bg-slate-100 text-slate-500 hover:text-slate-700"}`}>{l}</button>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {items.length === 0 ? (
            <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center text-sm text-slate-500">No articles published yet.</div>
          ) : (
            <>
              {lead && (
                <Link to={`/news/${lead.slug}`} data-testid="news-lead" className="group grid lg:grid-cols-2 gap-0 bg-white border border-slate-200 rounded-sm overflow-hidden mb-8 hover:border-[#0A192F] transition-colors">
                  <div className="bg-[#0A192F] min-h-[240px] relative">
                    {lead.cover_image_url ? <img src={lead.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="absolute inset-0 grid place-items-center"><Newspaper className="w-10 h-10 text-white/20" /></div>}
                  </div>
                  <div className="p-8 flex flex-col justify-center">
                    <span className="label-overline text-[#E63946]">{cats[lead.category] || lead.category}</span>
                    <h2 className="font-display text-3xl uppercase tracking-tight mt-2 group-hover:text-[#E63946]">{lead.title}</h2>
                    {lead.subtitle && <p className="text-slate-500 mt-2">{lead.subtitle}</p>}
                    <div className="text-xs text-slate-400 mt-4">{fmt(lead.published_at)} · {lead.author_name}</div>
                  </div>
                </Link>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {rest.map((a) => (
                  <Link key={a.id} to={`/news/${a.slug}`} data-testid={`news-card-${a.id}`} className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:border-[#0A192F] transition-colors">
                    <div className="aspect-[16/9] bg-gradient-to-br from-[#0A192F] to-[#1a2a4a] relative grid place-items-center">
                      {a.cover_image_url ? <img src={a.cover_image_url} alt="" className="absolute inset-0 w-full h-full object-cover" /> : <div className="text-center"><Newspaper className="w-8 h-8 text-white/25 mx-auto" /><span className="block text-[9px] uppercase tracking-widest text-white/30 mt-1">CBR Prospects</span></div>}
                    </div>
                    <div className="p-4">
                      <span className="label-overline text-[#E63946] text-[10px]">{cats[a.category] || a.category}</span>
                      <h3 className="font-semibold mt-1 group-hover:text-[#E63946] line-clamp-2">{a.title}</h3>
                      <div className="text-[11px] text-slate-400 mt-2">{fmt(a.published_at)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
